'use client';

import { publicEnv } from '@/lib/config/public-env';
import { useSession } from '@/providers/enhanced-session';
import { Button, Divider, Modal, ModalBody, ModalContent } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useStudioAI } from '../context';

export function FreemiumLimitModal({
    isOpen,
    onOpenChange,
    liveUsage,
}: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    liveUsage?: {
        hasReachedLimit: boolean;
        currentCount: number;
        limit: number;
        limitType: 'message' | 'token';
        daysUntilReset: number | null;
        limitMessage: string | null;
    };
}) {
    const { data: session } = useSession();
    const { freemiumLimitData } = useStudioAI();

    // Priority: mid-chat error data > live API query > session cache
    const sessionUsage = session?.user?.freemiumUsage;
    const currentCount =
        freemiumLimitData?.currentCount ??
        liveUsage?.currentCount ??
        sessionUsage?.currentCount ??
        0;
    const limit = freemiumLimitData?.limit ?? liveUsage?.limit ?? sessionUsage?.limit ?? 10;
    const limitType =
        freemiumLimitData?.limitType ??
        liveUsage?.limitType ??
        sessionUsage?.limitType ??
        'message';
    const daysUntilReset =
        freemiumLimitData?.daysUntilReset ??
        liveUsage?.daysUntilReset ??
        sessionUsage?.daysUntilReset;
    const limitMessage =
        freemiumLimitData?.limitMessage ?? liveUsage?.limitMessage ?? sessionUsage?.limitMessage;

    const unitLabel = limitType === 'token' ? 'free tokens' : 'free queries';

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            size="md"
            isDismissable={true}
            classNames={{
                base: 'rounded-2xl shadow-2xl',
                body: 'p-8',
            }}
        >
            <ModalContent>
                <ModalBody className="flex flex-col items-center text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-danger-50">
                        <Icon icon="lucide:lock" width={28} className="text-danger-400" />
                    </div>

                    <h2 className="mb-2 text-2xl font-bold leading-tight text-foreground">
                        You&apos;ve Reached Your{' '}
                        <span className="font-extrabold text-success-500">
                            {limitType === 'token' ? 'Free Token Limit' : 'Free Query Limit'}
                        </span>
                    </h2>

                    <p className="mb-6 text-small text-default-500">
                        {limitMessage ?? (
                            <>
                                You&apos;ve used{' '}
                                <span className="font-bold text-foreground">
                                    {currentCount.toLocaleString()}
                                </span>{' '}
                                of{' '}
                                <span className="font-bold text-foreground">
                                    {limit.toLocaleString()}
                                </span>{' '}
                                {unitLabel} this month. Unlock unlimited access by connecting with
                                our sales team for a personalized demo.
                            </>
                        )}
                    </p>

                    <Button
                        as="a"
                        href={publicEnv('NEXT_PUBLIC_FREEMIUM_CONTACT_SALES_URL')}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="lg"
                        radius="full"
                        className="w-full bg-gradient-to-tr from-mfour-green to-mfour-green/80 font-semibold text-white"
                        endContent={<Icon icon="lucide:arrow-right" width={18} />}
                    >
                        Contact Sales for a Demo
                    </Button>

                    <div className="my-4 flex w-full items-center gap-3">
                        <Divider className="flex-1" />
                        <span className="text-tiny uppercase tracking-wider text-default-400">
                            or
                        </span>
                        <Divider className="flex-1" />
                    </div>

                    {daysUntilReset != null && (
                        <div className="flex items-center gap-2 text-small text-default-500">
                            <Icon icon="lucide:clock" width={16} />
                            <span>
                                Come back in{' '}
                                <span className="font-bold text-foreground underline">
                                    {daysUntilReset} day{daysUntilReset === 1 ? '' : 's'}
                                </span>{' '}
                                when your {unitLabel} reset.
                            </span>
                        </div>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
